import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ErrorPage from '@/views/pages/404Page';

import {
  GuardedPageRoute,
  LazyLoad,
  NotFoundNavigationBinder,
  PathBasedPermissionRoute,
  ProtectedRoute,
  ProtectedLoginRoute,
  ProtectedPasswordChangeRoute,
  PublicRoute,
  DashboardOrHomeRoute,
  ReportsLandingRoute,
  StaffGuardedReportPageRoute,
} from './routeGuards';

import {
  BankManagementRoutes,
  BusinessCustomerDiscountRoute,
  BusinessGameAndPackageRoutes,
  BusinessManageCustomersRoute,
  BusinessManageUserRoutes,
  BusinessStreamerRoutes,
  CreditWalletRoutes,
  DistributorRoutes,
  GamesListRoutes,
  LineNotifyTargetsRoutes,
  PackageRoutes,
  SMSMonitorRoutes,
  BusinessSMSMonitorRoutes,
  StaffManageUserRoutes,
  SupplierListRoutes,
  TransactionAllRoutes,
} from './nestedRoutes';

import {
  AdjustCustomerCreditPage,
  ChangePasswordPage,
  ConfirmResetPasswordPage,
  CreateCreditTopUpTransactionPage,
  CreateDistributorPage,
  CreditTransactionPage,
  CreateCustomerDiscountTierPage,
  EditCustomerDiscountTierPage,
  EditDistributorPage,
  ForgotPasswordPage,
  LandingPageManagementPage,
  LoginPage,
  GarenaReportsPage,
  P2WReportsPage,
  ProfilePage,
  Register2FAPage,
  RegisterPage,
  ReportPage,
  SettingsPage,
  BusinessSettingsPage,
  TopupReportPage,
  TopUpGamesPage,
  TWOFAPage,
  OnlineMonitorPage,
  BotMonitorPage,
  BusinessProfitReportPage,
  StaffProfitReportPage,
  PartnerAPIKeyPage,
  PartnerAPIKeyCreatePage,
  PartnerDocsPage,
  StorefrontTemplatePage,
  CreateStreamerPage,
  EditStreamerPage,
  StreamerCommissionConfigPage,
  StreamerReportPage,
} from './lazyPages';

function App() {
  const { t } = useTranslation();

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <NotFoundNavigationBinder />
      <Routes>
        <Route path="/" element={<DashboardOrHomeRoute />} />
        <Route
          path="/distributor/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <DistributorRoutes />
            </PathBasedPermissionRoute>
          }
        />
        <Route
          path="/distributor/create"
          element={
            <GuardedPageRoute
              pageName={t('DISTRIBUTOR.CREATE.TITLE')}
              action="create"
            >
              <CreateDistributorPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/distributor/edit/:id"
          element={
            <GuardedPageRoute
              pageName={t('DISTRIBUTOR.EDIT.TITLE')}
              action="update"
            >
              <EditDistributorPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/staff/manage-accounts/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <StaffManageUserRoutes />
            </PathBasedPermissionRoute>
          }
        />
        <Route
          path="/staff/online-monitor"
          element={
            <GuardedPageRoute pageName={t('ONLINE_MONITOR.TITLE')}>
              <OnlineMonitorPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/staff/bot-monitor"
          element={
            <GuardedPageRoute pageName={t('BOT_MONITOR.TITLE')}>
              <BotMonitorPage />
            </GuardedPageRoute>
          }
        />
<Route
          path="/reports/garena"
          element={
            <StaffGuardedReportPageRoute
              pageName={t('STAFF_SIDEBAR.REPORT_GARENA')}
            >
              <GarenaReportsPage />
            </StaffGuardedReportPageRoute>
          }
        />
        <Route
          path="/reports/p2w"
          element={
            <StaffGuardedReportPageRoute
              pageName={t('STAFF_SIDEBAR.REPORT_P2W')}
            >
              <P2WReportsPage />
            </StaffGuardedReportPageRoute>
          }
        />
        <Route
          path="/reports/topup"
          element={
            <StaffGuardedReportPageRoute
              pageName={t('STAFF_SIDEBAR.REPORT_TOPUP')}
            >
              <TopupReportPage />
            </StaffGuardedReportPageRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <GuardedPageRoute pageName={t('STAFF_SIDEBAR.REPORT')}>
              <ReportsLandingRoute />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/reports/profit"
          element={
            <GuardedPageRoute pageName={t('BUSINESS_PROFIT_REPORT.TITLE')}>
              <LazyLoad>
                <BusinessProfitReportPage />
              </LazyLoad>
            </GuardedPageRoute>
          }
        />
        <Route
          path="/reports/profit-overview"
          element={
            <StaffGuardedReportPageRoute
              pageName={t('STAFF_SIDEBAR.REPORT_PROFIT_OVERVIEW')}
            >
              <StaffProfitReportPage />
            </StaffGuardedReportPageRoute>
          }
        />
        <Route
          path="/game-list/*"
          element={
            <GuardedPageRoute pageName={t('STAFF_SIDEBAR.GAME_LIST')}>
              <GamesListRoutes />
            </GuardedPageRoute>
          }
        />
<Route
          path="/business-topup"
          element={
            <GuardedPageRoute pageName={t('BUSINESS_TOPUP')}>
              <TopUpGamesPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/business-credit-transaction"
          element={
            <GuardedPageRoute
              pageName={t('BUSINESS_SIDEBAR.CREDIT_TOPUP_TRANSACTION')}
            >
              <CreditTransactionPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/business-credit-transaction/credit/form"
          element={
            <GuardedPageRoute
              pageName={t('BUSINESS_SIDEBAR.CREDIT_TOPUP_TRANSACTION')}
              action="create"
            >
              <CreateCreditTopUpTransactionPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/business-credit-transaction/adjust-customer-credit"
          element={
            <GuardedPageRoute
              pageName={t('BUSINESS_SIDEBAR.CREDIT_TOPUP_TRANSACTION')}
              action="create"
            >
              <AdjustCustomerCreditPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/package/*"
          element={
            <GuardedPageRoute pageName={t('STAFF_SIDEBAR.PACKAGE_LIST')}>
              <PackageRoutes />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/credit-wallet/*"
          element={
            <GuardedPageRoute pageName={t('STAFF_SIDEBAR.CREDIT_WALLET')}>
              <CreditWalletRoutes />
            </GuardedPageRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute pageName={t('SIDEBAR.PROFILE')}>
              <LazyLoad>
                <ProfilePage />
              </LazyLoad>
            </ProtectedRoute>
          }
        />
        <Route
          path="/transaction-all/*"
          element={
            <GuardedPageRoute pageName={t('TRANSACTION_ALL')}>
              <TransactionAllRoutes />
            </GuardedPageRoute>
          }
        />
        {/* <Route
          path="/server-list/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <ProtectedRoute pageName={t('STAFF_SIDEBAR.SERVER_LIST')}>
                <ServerListRoutes />
              </ProtectedRoute>
            </PathBasedPermissionRoute>
          }
        /> */}
        <Route
          path="/supplier/*"
          element={
            <GuardedPageRoute pageName={t('STAFF_SIDEBAR.SUPPLIER_LIST')}>
              <SupplierListRoutes />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/line-notify-targets/*"
          element={
            <GuardedPageRoute pageName={t('STAFF_SIDEBAR.LINE_NOTIFY_TARGETS')}>
              <LineNotifyTargetsRoutes />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/sms-monitor/*"
          element={
            <GuardedPageRoute pageName={t('STAFF_SIDEBAR.SMS_MONITOR')}>
              <SMSMonitorRoutes />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <GuardedPageRoute pageName={t('STAFF_SIDEBAR.SETTINGS')}>
              <SettingsPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/business/manage-accounts/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <BusinessManageUserRoutes />
            </PathBasedPermissionRoute>
          }
        />
        <Route
          path="/manage-customers/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <BusinessManageCustomersRoute />
            </PathBasedPermissionRoute>
          }
        />
        <Route
          path="/customer-discounts/create"
          element={
            <GuardedPageRoute
              pageName={t('CUSTOMER_DISCOUNT_TIER.CREATE.TITLE')}
              action="update"
            >
              <CreateCustomerDiscountTierPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/customer-discounts/edit/:id"
          element={
            <GuardedPageRoute
              pageName={t('CUSTOMER_DISCOUNT_TIER.EDIT.TITLE')}
              action="update"
            >
              <EditCustomerDiscountTierPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/customer-discounts/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <BusinessCustomerDiscountRoute />
            </PathBasedPermissionRoute>
          }
        />
        <Route
          path="/game-and-package/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <BusinessGameAndPackageRoutes />
            </PathBasedPermissionRoute>
          }
        />
        <Route
          path="/bank-management/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <BankManagementRoutes />
            </PathBasedPermissionRoute>
          }
        />
        <Route
          path="/landing-page"
          element={
            <GuardedPageRoute pageName={t('BUSINESS_SIDEBAR.LANDING_PAGE')}>
              <LandingPageManagementPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/business-settings"
          element={
            <GuardedPageRoute pageName={t('BUSINESS_SIDEBAR.SETTINGS')}>
              <BusinessSettingsPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/partner-api-keys/create"
          element={
            <GuardedPageRoute pageName={t('BUSINESS_SIDEBAR.PARTNER_API_KEYS')}>
              <PartnerAPIKeyCreatePage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/partner-api-keys"
          element={
            <GuardedPageRoute pageName={t('BUSINESS_SIDEBAR.PARTNER_API_KEYS')}>
              <PartnerAPIKeyPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/partner-docs/en"
          element={
            <ProtectedRoute>
              <LazyLoad>
                <PartnerDocsPage />
              </LazyLoad>
            </ProtectedRoute>
          }
        />
        <Route
          path="/partner-docs/th"
          element={
            <ProtectedRoute>
              <LazyLoad>
                <PartnerDocsPage />
              </LazyLoad>
            </ProtectedRoute>
          }
        />
        <Route path="/partner-docs" element={<Navigate to="/partner-docs/en" replace />} />
        <Route
          path="/business-qr-monitor/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <BusinessSMSMonitorRoutes />
            </PathBasedPermissionRoute>
          }
        />
        <Route
          path="/storefront-template"
          element={
            <ProtectedRoute pageName={t('BUSINESS_SIDEBAR.STOREFRONT_TEMPLATE')}>
              <LazyLoad>
                <StorefrontTemplatePage />
              </LazyLoad>
            </ProtectedRoute>
          }
        />
        <Route
          path="/streamers/create"
          element={
            <GuardedPageRoute
              pageName={t('STREAMER_MANAGEMENT.CREATE.TITLE')}
              action="create"
            >
              <CreateStreamerPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/streamers/edit/:id"
          element={
            <GuardedPageRoute
              pageName={t('STREAMER_MANAGEMENT.EDIT.TITLE')}
              action="update"
            >
              <EditStreamerPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/streamers/:id/commissions"
          element={
            <GuardedPageRoute
              pageName={t('STREAMER_MANAGEMENT.COMMISSION_CONFIG.TITLE')}
              action="update"
            >
              <StreamerCommissionConfigPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/streamers/:id/report"
          element={
            <GuardedPageRoute
              pageName={t('STREAMER_MANAGEMENT.REPORT.TITLE')}
              action="read"
            >
              <StreamerReportPage />
            </GuardedPageRoute>
          }
        />
        <Route
          path="/streamers/*"
          element={
            <PathBasedPermissionRoute fallbackPath="/">
              <BusinessStreamerRoutes />
            </PathBasedPermissionRoute>
          }
        />
        {/* Disabled: company-management path */}
        <Route
          path="/company-management"
          element={<Navigate to="/landing-page" replace />}
        />

        <Route
          path="/login/2FA"
          element={
            <ProtectedLoginRoute>
              <LazyLoad>
                <TWOFAPage />
              </LazyLoad>
            </ProtectedLoginRoute>
          }
        />
        <Route
          path="/login/register-2fa"
          element={
            <ProtectedLoginRoute>
              <LazyLoad>
                <Register2FAPage />
              </LazyLoad>
            </ProtectedLoginRoute>
          }
        />
        <Route
          path="/login/change-password"
          element={
            <ProtectedPasswordChangeRoute>
              <LazyLoad>
                <ChangePasswordPage />
              </LazyLoad>
            </ProtectedPasswordChangeRoute>
          }
        />
        <Route
          path="/login"
          element={
            <LazyLoad>
              <LoginPage />
            </LazyLoad>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <LazyLoad>
              <ForgotPasswordPage />
            </LazyLoad>
          }
        />
        <Route
          path="/confirm-reset-password"
          element={
            <PublicRoute>
              <LazyLoad>
                <ConfirmResetPasswordPage />
              </LazyLoad>
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <LazyLoad>
                <RegisterPage />
              </LazyLoad>
            </PublicRoute>
          }
        />
        <Route path="/404" element={<ErrorPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </Router>
  );
}

export default App;
